/**
 * MELANUA Shop API Utilities
 * Handles all API calls for cart, checkout, and authentication
 */

const API_BASE = '/api';

// Token management
const getAccessToken = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');
const setTokens = (access, refresh) => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
};
const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
};

// Authenticated fetch wrapper
const authFetch = async (url, options = {}) => {
    const token = getAccessToken();
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Include credentials for session-based cart
    let response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include'  // Send cookies with request
    });

    // If 401, try to refresh token
    if (response.status === 401 && getRefreshToken()) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            headers['Authorization'] = `Bearer ${getAccessToken()}`;
            response = await fetch(url, {
                ...options,
                headers,
                credentials: 'include'
            });
        }
    }

    return response;
};

// Refresh access token
const refreshAccessToken = async () => {
    try {
        const response = await fetch(`${API_BASE}/auth/token/refresh/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: getRefreshToken() }),
        });

        if (response.ok) {
            const data = await response.json();
            localStorage.setItem('access_token', data.access);
            return true;
        }
    } catch (error) {
        console.error('Token refresh failed:', error);
    }

    clearTokens();
    return false;
};

// =============================================================================
// CART API
// =============================================================================

export const cartApi = {
    // Get current cart
    get: async () => {
        const response = await authFetch(`${API_BASE}/shop/cart/`);
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
    },

    // Add item to cart
    addItem: async (productId, variantId = null, quantity = 1) => {
        const response = await authFetch(`${API_BASE}/shop/cart/items/`, {
            method: 'POST',
            body: JSON.stringify({
                product_id: productId,
                variant_id: variantId,
                quantity,
            }),
        });
        if (!response.ok) {
            // Handle non-JSON error responses
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const error = await response.json();
                throw new Error(error.detail || error.variant_id?.[0] || error.error || 'Failed to add item');
            }
            throw new Error(`Server error (${response.status}). Please try again.`);
        }
        return response.json();
    },

    // Update item quantity
    updateItem: async (itemId, quantity) => {
        const response = await authFetch(`${API_BASE}/shop/cart/items/${itemId}/`, {
            method: 'PATCH',
            body: JSON.stringify({ quantity }),
        });
        if (!response.ok) throw new Error('Failed to update item');
        return response.json();
    },

    // Remove item from cart
    removeItem: async (itemId) => {
        const response = await authFetch(`${API_BASE}/shop/cart/items/${itemId}/`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to remove item');
        return response.json();
    },

    // Clear cart
    clear: async () => {
        const response = await authFetch(`${API_BASE}/shop/cart/`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to clear cart');
        return response.json();
    },
};

// =============================================================================
// CHECKOUT API
// =============================================================================

export const checkoutApi = {
    // Get shipping methods
    getShippingMethods: async () => {
        const response = await authFetch(`${API_BASE}/shop/shipping-methods/`);
        if (!response.ok) throw new Error('Failed to fetch shipping methods');
        return response.json();
    },

    // Start checkout
    startCheckout: async (checkoutData) => {
        const response = await authFetch(`${API_BASE}/shop/checkout/`, {
            method: 'POST',
            body: JSON.stringify(checkoutData),
        });
        if (!response.ok) {
            const error = await response.json();
            // Handle field-specific validation errors
            if (error.shipping_method_id) {
                throw new Error(`Shipping method: ${error.shipping_method_id[0]}`);
            }
            if (error.email) {
                throw new Error(`Email: ${error.email[0]}`);
            }
            if (error.shipping_address) {
                const firstError = Object.values(error.shipping_address)[0];
                throw new Error(`Address: ${Array.isArray(firstError) ? firstError[0] : firstError}`);
            }
            throw new Error(error.detail || error.error || error.non_field_errors?.[0] || 'Checkout failed. Please check your information.');
        }
        return response.json();
    },

    // Create Stripe Checkout Session (redirects to Stripe hosted checkout)
    createCheckoutSession: async (orderId, successUrl, cancelUrl) => {
        const response = await authFetch(`${API_BASE}/shop/checkout/session/`, {
            method: 'POST',
            body: JSON.stringify({
                order_id: orderId,
                success_url: successUrl,
                cancel_url: cancelUrl,
            }),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || error.error || 'Failed to create checkout session');
        }
        return response.json();
    },

    // Create payment intent (legacy - for custom card form)
    createPaymentIntent: async (orderId) => {
        const response = await authFetch(`${API_BASE}/shop/checkout/payment-intent/`, {
            method: 'POST',
            body: JSON.stringify({ order_id: orderId }),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || error.error || 'Payment setup failed');
        }
        return response.json();
    },

    // Confirm payment
    confirmPayment: async (paymentIntentId) => {
        const response = await authFetch(`${API_BASE}/shop/checkout/confirm/`, {
            method: 'POST',
            body: JSON.stringify({ payment_intent_id: paymentIntentId }),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || error.error || 'Payment confirmation failed');
        }
        return response.json();
    },
};

// =============================================================================
// ORDER API
// =============================================================================

export const orderApi = {
    // Get user orders (authenticated)
    list: async () => {
        const response = await authFetch(`${API_BASE}/shop/orders/`);
        if (!response.ok) throw new Error('Failed to fetch orders');
        return response.json();
    },

    // Get order details
    get: async (orderId) => {
        const response = await authFetch(`${API_BASE}/shop/orders/${orderId}/`);
        if (!response.ok) throw new Error('Order not found');
        return response.json();
    },

    // Lookup order by number and email (for guests)
    lookup: async (orderNumber, email) => {
        const response = await fetch(`${API_BASE}/shop/orders/lookup/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_number: orderNumber, email }),
        });
        if (!response.ok) throw new Error('Order not found');
        return response.json();
    },
};

// =============================================================================
// AUTH API
// =============================================================================

export const authApi = {
    // Register new user
    register: async (userData) => {
        const response = await fetch(`${API_BASE}/shop/auth/register/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(JSON.stringify(error));
        }
        const data = await response.json();
        setTokens(data.tokens.access, data.tokens.refresh);
        return data;
    },

    // Login
    login: async (email, password) => {
        const response = await fetch(`${API_BASE}/auth/token/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: email, password }),
        });
        if (!response.ok) {
            throw new Error('Invalid email or password');
        }
        const data = await response.json();
        setTokens(data.access, data.refresh);
        return data;
    },

    // Logout
    logout: () => {
        clearTokens();
    },

    // Get current user
    me: async () => {
        const response = await authFetch(`${API_BASE}/shop/auth/me/`);
        if (!response.ok) return null;
        return response.json();
    },

    // Update profile
    updateProfile: async (data) => {
        const response = await authFetch(`${API_BASE}/shop/auth/me/`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to update profile');
        }
        return response.json();
    },

    // Request password reset
    requestPasswordReset: async (email) => {
        const response = await fetch(`${API_BASE}/shop/auth/password-reset/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to send reset email');
        }
        return response.json();
    },

    // Confirm password reset
    confirmPasswordReset: async (uid, token, password, password_confirm) => {
        const response = await fetch(`${API_BASE}/shop/auth/password-reset/confirm/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, token, password, password_confirm }),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to reset password');
        }
        return response.json();
    },

    // Check if user is logged in
    isLoggedIn: () => {
        return !!getAccessToken();
    },

    // Get tokens (for external use)
    getTokens: () => ({
        access: getAccessToken(),
        refresh: getRefreshToken(),
    }),
};

// =============================================================================
// REVIEW API
// =============================================================================

export const reviewApi = {
    // Get approved reviews for a product
    list: async (slug) => {
        const response = await fetch(`${API_BASE}/content/products/${slug}/reviews/`);
        if (!response.ok) throw new Error('Failed to fetch reviews');
        return response.json();
    },

    // Check if current user can review (authenticated)
    checkEligibility: async (slug) => {
        const response = await authFetch(`${API_BASE}/content/products/${slug}/reviews/eligibility/`);
        if (!response.ok) throw new Error('Failed to check eligibility');
        return response.json();
    },

    // Create a review (authenticated)
    create: async (slug, reviewData) => {
        const response = await authFetch(`${API_BASE}/content/products/${slug}/reviews/create/`, {
            method: 'POST',
            body: JSON.stringify(reviewData),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || error.detail || 'Failed to submit review');
        }
        return response.json();
    },
};

export default {
    cart: cartApi,
    checkout: checkoutApi,
    order: orderApi,
    auth: authApi,
    review: reviewApi,
};
